import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { Button } from '@/components/atoms'
import { BirthDatePicker } from '@/components/organisms/customer/_detail/birth-date-picker'
import { genders } from '@/libs/genders'
import { occupationsWithOthers } from '@/libs/occupations'
import { countryList } from '@/libs/nationality'
import { RadioGroupField } from '@/components/molecules/radio-group-field'
import { isEmployee } from '@/models/self-checkin/accommodation-info'
import { ButtonType } from '@/components/atoms/button'
import * as api from '@/apis/aipass'
import { Controller, useFormContext, UseFormReturn, useWatch } from 'react-hook-form'
import { Select } from '@/components/atoms/select'
import { Select as AntSelect } from 'antd'
import { InputField } from '@/components/molecules/input-field'
import { GuestInput, UpdateGuestParams } from '@/models/guest-input'

export const EditForm: React.FC = () => {
  const { t } = useTranslation()
  const { control, setValue } = useFormContext<GuestInput>()
  const watchNationality = useWatch({ control, name: 'nationality' })
  const watchOccupation = useWatch({ control, name: 'occupation' })
  const [zipFetchLoading, setZipFetchLoading] = useState(false)

  const isOtherNationality = () => watchNationality && watchNationality !== 'JPN'

  const isOtherOccupation = () => watchOccupation && watchOccupation === 'その他'

  const fetchZipCode = async (value: string) => {
    try {
      if (zipFetchLoading) {
        return
      }
      setZipFetchLoading(true)
      const response = await api.fetchZipCode(value)
      const result = response?.results[0]
      setValue('address', `${result?.address1}${result?.address2}${result?.address3}`)
    } catch {
      // nothing todo
    } finally {
      setZipFetchLoading(false)
    }
  }

  return (
    <div css={formStyle}>
      <div>
        <p css={guestInfoLabelStyle}>{t('Residence')}</p>
        <Controller
          name="nationality"
          control={control}
          render={({ field }) => (
            <RadioGroupField
              name={field.name}
              value={isOtherNationality() ? 'NON' : field.value}
              onChange={field.onChange}
              items={[
                { value: 'JPN', label: t('Japanese nationality') },
                { value: 'NON', label: t('Foreign nationality') },
              ]}
              style={{ radioSize: 18, labelMargin: 8 }}
              groupCss={{ marginBottom: 16 }}
              itemWrapperCss={radioButtonWrapperStyle}
              itemCss={radioButtonStyle}
            />
          )}
        />
        {isOtherNationality() && (
          <Controller
            name="nationality"
            control={control}
            render={({ field }) => (
              <Select value={watchNationality === 'NON' ? '' : field.value} onChange={field.onChange}>
                <AntSelect.Option value="">{t('Select country')}</AntSelect.Option>
                {countryList('ja', false).map(country => (
                  <AntSelect.Option value={country.value} key={country.value}>
                    {country.name}
                  </AntSelect.Option>
                ))}
              </Select>
            )}
          />
        )}
      </div>

      <div>
        <p css={guestInfoLabelStyle}>{t('Full name')}</p>
        <div className="flex-input-block">
          <Controller
            name="lastName"
            control={control}
            render={({ field }) => (
              <InputField
                type="text"
                fieldWidth="fit-content"
                width="183px"
                value={field.value}
                onChange={field.onChange}
                placeholder={t('Doe')}
              />
            )}
          />
          <Controller
            name="firstName"
            control={control}
            render={({ field }) => (
              <InputField
                type="text"
                fieldWidth="fit-content"
                width="183px"
                value={field.value}
                onChange={field.onChange}
                placeholder={t('John')}
              />
            )}
          />
        </div>
      </div>

      <div>
        <p css={guestInfoLabelStyle}>{t('Furigana')}</p>
        <div className="flex-input-block">
          <Controller
            name="lastNameKana"
            control={control}
            render={({ field }) => (
              <InputField
                type="text"
                fieldWidth="fit-content"
                width="183px"
                value={field.value}
                onChange={field.onChange}
                placeholder={t('Surname')}
              />
            )}
          />
          <Controller
            name="firstNameKana"
            control={control}
            render={({ field }) => (
              <InputField
                type="text"
                fieldWidth="fit-content"
                width="183px"
                value={field.value}
                onChange={field.onChange}
                placeholder={t('May')}
              />
            )}
          />
        </div>
      </div>

      <div>
        <p css={guestInfoLabelStyle}>{t('Date of birth')}</p>
        <Controller
          name="birthDate"
          control={control}
          render={({ field }) => (
            <div style={{ marginBottom: 16 }}>
              <BirthDatePicker value={field.value} placeholder={t('Please select')} onChange={field.onChange} />
            </div>
          )}
        />
      </div>

      <div>
        <p css={guestInfoLabelStyle}>{t('Sex')}</p>
        <Controller
          name="gender"
          control={control}
          render={({ field }) => (
            <RadioGroupField
              value={field.value}
              name={field.name}
              items={genders}
              style={{ radioSize: 18, labelMargin: 8 }}
              groupCss={{ marginBottom: 16 }}
              itemWrapperCss={radioButtonWrapperStyle}
              itemCss={radioButtonStyle}
              onChange={field.onChange}
            />
          )}
        />
      </div>

      <div>
        <p css={guestInfoLabelStyle}>{t('Post code')}</p>
        <Controller
          name="postalCode"
          control={control}
          render={({ field }) => (
            <div className="flex-input-block">
              <InputField
                type="tel"
                fieldWidth="fit-content"
                width="100px"
                value={field.value}
                onChange={field.onChange}
                placeholder="1234567"
              />
              <Button
                buttonType={ButtonType.Secondary}
                width={140}
                height={32}
                fontWeight="normal"
                loading={zipFetchLoading}
                onClick={() => field.value && fetchZipCode(field.value)}
              >
                {t('Search by Address')}
              </Button>
            </div>
          )}
        />
      </div>
      <div>
        <p css={guestInfoLabelStyle}>{t('Address')}</p>
        <Controller
          name="address"
          control={control}
          render={({ field }) => (
            <InputField type="text" value={field.value} onChange={field.onChange} placeholder={t('1-1 Chiyoda Chiyoda-ku')} />
          )}
        />
      </div>
      <div>
        <p css={guestInfoLabelStyle}>{t('Phone number')}</p>
        <Controller
          name="telephone"
          control={control}
          rules={{
            pattern: { value: /^\d+$/, message: t('Please enter numbers only, without symbols or spaces') },
          }}
          render={({ field, fieldState: { error } }) => (
            <InputField type="text" value={field.value} onChange={field.onChange} placeholder="00000000000" error={error?.message} />
          )}
        />
      </div>
      <div>
        <p css={guestInfoLabelStyle}>{t('Email address')}</p>
        <Controller
          name="email"
          control={control}
          render={({ field }) => <InputField type="text" value={field.value} onChange={field.onChange} placeholder="xxx@cuicin.co.jp" />}
        />
      </div>
      <div>
        <p css={guestInfoLabelStyle}>{t('Occupation')}</p>
        <Controller
          name="occupation"
          control={control}
          render={({ field }) => (
            <Select value={field.value} onChange={field.onChange}>
              <AntSelect.Option value="">{t('Please select')}</AntSelect.Option>
              {occupationsWithOthers.map(occupation => (
                <AntSelect.Option value={t(occupation.label)} key={occupation.value}>
                  {t(occupation.label)}
                </AntSelect.Option>
              ))}
            </Select>
          )}
        />

        {isOtherOccupation() && (
          <Controller
            name="otherOccupation"
            control={control}
            render={({ field }) => <InputField type="text" value={field.value} onChange={field.onChange} />}
          />
        )}
      </div>

      {isEmployee(watchOccupation) && (
        <div>
          <p css={guestInfoLabelStyle}>{t('Company name')}</p>
          <Controller
            name="company"
            control={control}
            render={({ field }) => (
              <InputField type="text" value={field.value} onChange={field.onChange} placeholder={t('CUICIN Co Ltd')} />
            )}
          />
        </div>
      )}

      <div>
        <p css={guestInfoLabelStyle}>{t('E-mail delivery permission')}</p>
        <Controller
          name="deliveryAgreementStatus"
          control={control}
          render={({ field }) => (
            <Select value={field.value} onChange={field.onChange} customStyle={css({ paddingBottom: 0 })}>
              <AntSelect.Option value="unconfirmed">{t('Unconfirmed')}</AntSelect.Option>
              <AntSelect.Option value="deliverable">{t('Deliverable')}</AntSelect.Option>
              <AntSelect.Option value="nonDeliverable">{t('Non deliverable')}</AntSelect.Option>
            </Select>
          )}
        />
      </div>
    </div>
  )
}

const formStyle = css({
  '.flex-input-block': { display: 'flex', gap: 16 },
})

const guestInfoLabelStyle = css({
  marginBottom: 12,
  color: '#676767',
  fontSize: 12,
  fontWeight: 'bold',
  letterSpacing: '0.6px',
})

const radioButtonWrapperStyle = css({ '& + &': { marginLeft: 40 } })
const radioButtonStyle = css({
  label: {
    color: '#272727',
    fontWeight: 'normal',
    fontSize: '14px',
    lineHeight: '20px',
    letterSpacing: '0.9px',
  },
})
